import * as R from "ramda";
import * as Nested from "@/util/Nested";
import {
  SEARCH_DATES,
  getFormattedDate,
  getInputObject,
  getInputDates,
  processCalendarDates,
  getCalendarKey,
  SEARCH_INPUT_PLACEHOLDER,
  getDefaultSearchDates,
} from "./Search.dates.util";
import * as selectors from "./DatePicker.selectors";
import { getMomentDate } from "@/util/dates";
import { currentUserIsCitybaseAdmin } from "@/components/currentUser/CurrentUser.state";

export const SEARCH_CHANGE_MONTH = "transactionSearchDatePicker/CHANGE_MONTH";
export const changeMonth = (month) => ({
  type: SEARCH_CHANGE_MONTH,
  payload: { month },
});

export const OPEN_DATES_MODAL = "transactionSearchDatePicker/OPEN_DATES_MODAL";
export const openDatesModal = (focusedInput) => ({
  type: OPEN_DATES_MODAL,
  payload: { focusedInput },
});

export const CLOSE_DATES_MODAL = "transactionSearchDatePicker/CLOSE_DATES_MODAL";
export const closeDatesModal = () => ({
  type: CLOSE_DATES_MODAL,
});

export const SEARCH_BLUR_INPUT_DATE = "transactionSearchDatePicker/BLUR_INPUT_DATE";
export const blurInputDate = (date, token) => ({
  type: SEARCH_BLUR_INPUT_DATE,
  payload: { date, token },
});

export const SEARCH_CHANGE_INPUT_DATE =
  "transactionSearchDatePicker/CHANGE_INPUT_DATE";
export const changeInputDate = (date, token) => ({
  type: SEARCH_CHANGE_INPUT_DATE,
  payload: { date, token },
});

export const SEARCH_CHANGE_CALENDAR_DATES =
  "transactionSearchDatePicker/CHANGE_CALENDAR_DATES";
export const changeCalendarDates = (dates, process = false, preset = false) => ({
  type: SEARCH_CHANGE_CALENDAR_DATES,
  payload: { dates, process, preset },
});

export const SEARCH_FOCUS_START_INPUT =
  "transactionSearchDatePicker/FOCUS_START_INPUT";
export const focusStartDateInput = (token) => ({
  type: SEARCH_FOCUS_START_INPUT,
  payload: { token },
});

export const SEARCH_FOCUS_END_INPUT = "transactionSearchDatePicker/FOCUS_END_INPUT";
export const focusEndDateInput = (token) => ({
  type: SEARCH_FOCUS_END_INPUT,
  payload: { token },
});

// A factory, not a constant, so each fresh slice gets the current date instead
// of the date the bundle was loaded.
const getInitialState = () => {
  const defaultSearchDates = getDefaultSearchDates();
  return {
    dates: {
      isOpen: false,
      focusedInput: SEARCH_DATES.START,
      startPlaceholder: SEARCH_INPUT_PLACEHOLDER.START,
      endPlaceholder: SEARCH_INPUT_PLACEHOLDER.END,
      visibleMonth: getMomentDate().startOf("day"),
      calendarKey: 0,
      input: {
        startDate: getFormattedDate(defaultSearchDates.startDate),
        endDate: getFormattedDate(defaultSearchDates.endDate),
      },
      calendar: {
        startDate: defaultSearchDates.startDate,
        endDate: defaultSearchDates.endDate,
      },
    },
  };
};

const _reducer = (state = getInitialState(), action = {}) => {
  const { payload } = action;
  switch (action.type) {
    case SEARCH_CHANGE_MONTH:
      return R.assocPath(["dates", "visibleMonth"], payload.month, state);
    case OPEN_DATES_MODAL:
      return {
        ...state,
        dates: {
          ...state.dates,
          isOpen: true,
          focusedInput: R.pathOr(
            state.dates.focusedInput,
            ["payload", "focusedInput"],
            action
          ),
        },
      };
    case CLOSE_DATES_MODAL:
      return {
        ...state,
        dates: {
          ...state.dates,
          isOpen: false,
        },
      };
    case SEARCH_CHANGE_CALENDAR_DATES:
      return (() => {
        const { startDate, endDate } = payload.process
          ? processCalendarDates(
              selectors.anchorDate({ transactionSearchDatePicker: state }),
              payload.dates
            )
          : payload.dates;

        const currentMonth = (
          endDate ||
          startDate ||
          state.dates.visibleMonth
        ).startOf("day");

        return R.assoc(
          "dates",
          R.mergeRight(state.dates, {
            calendar: { startDate, endDate },
            input: {
              startDate: getFormattedDate(startDate),
              endDate: getFormattedDate(endDate),
            },
            visibleMonth: currentMonth,
            calendarKey: getCalendarKey(currentMonth, state.dates),
          }),
          state
        );
      })();
    case SEARCH_CHANGE_INPUT_DATE:
      return (() => {
        const newPayload = selectors.getCalendarDates(state, {
          date: payload.date,
          token: payload.token,
        });

        return R.assoc(
          "dates",
          R.mergeRight(state.dates, {
            input: getInputObject(newPayload),
            calendar: newPayload.dates,
          }),
          state
        );
      })();
    case SEARCH_BLUR_INPUT_DATE:
      return R.assoc(
        "dates",
        R.mergeRight(state.dates, {
          input: R.mergeRight(
            state.dates.input,
            getInputDates(payload.token, payload.date, state)
          ),
          startPlaceholder: SEARCH_INPUT_PLACEHOLDER.START,
          endPlaceholder: SEARCH_INPUT_PLACEHOLDER.END,
        }),
        state
      );
    case SEARCH_FOCUS_START_INPUT:
      return R.assoc(
        "dates",
        R.mergeRight(state.dates, {
          startPlaceholder: SEARCH_INPUT_PLACEHOLDER.ACTIVE,
          focusedInput: SEARCH_DATES.START,
        }),
        state
      );
    case SEARCH_FOCUS_END_INPUT:
      return R.assoc(
        "dates",
        R.mergeRight(state.dates, {
          endPlaceholder: SEARCH_INPUT_PLACEHOLDER.ACTIVE,
          focusedInput: SEARCH_DATES.END,
        }),
        state
      );
    default:
      return state;
  }
};

export const _mapStateToProps = (state) => ({
  search: selectors.datesState(state),
  visibleMonth: selectors.visibleMonth(state),
  isCitybaseAdmin: currentUserIsCitybaseAdmin(state),
  focusedInput: selectors.getFocusedInput(state),
});

export const _mapDispatchToProps = (dispatch) => ({
  changeMonth: (month) => dispatch(changeMonth(month)),
  setPresetDate: (dates) => dispatch(changeCalendarDates(dates, false, true)),
  changeCalendarDates: (dates) => dispatch(changeCalendarDates(dates, true)),
  clearDates: () =>
    dispatch(changeCalendarDates({ startDate: null, endDate: null })),
  openDatesModal: (evt) => dispatch(openDatesModal(evt.target.dataset.token)),
  // The calendar's own close action. A host that opts into Enter-to-close
  // calls it from the modal's keydown handler (WCHK-3083 review).
  closeDatesModal: () => dispatch(closeDatesModal()),
  changeInputStartDate: (inputStartValue) =>
    dispatch(changeInputDate(inputStartValue, SEARCH_DATES.START)),
  changeInputEndDate: (inputEndValue) =>
    dispatch(changeInputDate(inputEndValue, SEARCH_DATES.END)),
  blurInputStartDate: (inputStartValue) =>
    dispatch(blurInputDate(inputStartValue, SEARCH_DATES.START)),
  blurInputEndDate: (inputEndValue) =>
    dispatch(blurInputDate(inputEndValue, SEARCH_DATES.END)),
  focusInputStart: (evt) => dispatch(focusStartDateInput(evt.target.dataset.token)),
  focusInputEnd: (evt) => dispatch(focusEndDateInput(evt.target.dataset.token)),
});

const {
  reducer: nestedReducer,
  mapStateToProps,
  mapDispatchToProps,
  actions,
} = Nested.nestStates(
  {
    reducer: _reducer,
    mapStateToProps: _mapStateToProps,
    mapDispatchToProps: _mapDispatchToProps,
  },
  {
    forms: {},
    alertBars: {},
  },
  "transactionSearchDatePicker"
);

const reducer = (state, action) => nestedReducer(state, action);

export { reducer, mapStateToProps, mapDispatchToProps, actions };
